/**
 * YouTube URL utilities — validation, ID extraction, embed URL generation.
 */

const YT_PATTERNS = [
  /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
  /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
];

/**
 * Extracts the YouTube video ID from a URL.
 * Returns null if the URL is not a recognised YouTube format.
 */
export function extractYouTubeId(url: string): string | null {
  if (!url) return null;
  for (const pattern of YT_PATTERNS) {
    const match = url.match(pattern);
    if (match?.[1]) return match[1];
  }
  return null;
}

/**
 * Returns true if the URL is a valid YouTube video URL.
 */
export function isValidYouTubeUrl(url: string): boolean {
  return extractYouTubeId(url) !== null;
}

/**
 * Returns the embed URL for a YouTube video ID.
 * e.g. "https://www.youtube.com/embed/dQw4w9WgXcQ?rel=0"
 */
export function getYouTubeEmbedUrl(videoId: string): string {
  return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`;
}

/**
 * Returns the thumbnail URL for a YouTube video.
 * Uses the high-quality thumbnail (hqdefault).
 */
export function getYouTubeThumbnailUrl(videoId: string): string {
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
}

/**
 * Validates a YouTube URL and returns both the ID and embed URL if valid.
 */
export function parseYouTubeUrl(url: string): {
  valid: boolean;
  videoId: string | null;
  embedUrl: string | null;
  thumbnailUrl: string | null;
} {
  const videoId = extractYouTubeId(url);
  if (!videoId) {
    return { valid: false, videoId: null, embedUrl: null, thumbnailUrl: null };
  }
  return {
    valid:        true,
    videoId,
    embedUrl:     getYouTubeEmbedUrl(videoId),
    thumbnailUrl: getYouTubeThumbnailUrl(videoId),
  };
}
